import React, { useEffect, useState } from 'react';
import {
    Popover,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    CircularProgress,
    Divider,
    Collapse,
    Grid,
    Chip
} from '@mui/material';
import { History, ExpandMore, ExpandLess } from '@mui/icons-material';
import { axiosGraphQLQuery, getSession } from '../Auth/AuthHandler';

const ActivityLogPopover = ({ departmentId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const [reportDetails, setReportDetails] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [uuidColorMap, setUuidColorMap] = useState({});

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        if (!anchorEl) {
            loadActivities();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Color palette for UUIDs - distinct colors for easy tracking
    const colorPalette = [
        '#FF6B6B', // Red
        '#4ECDC4', // Teal
        '#45B7D1', // Blue
        '#FFA07A', // Light Salmon
        '#98D8C8', // Mint
        '#F7DC6F', // Yellow
        '#BB8FCE', // Purple
        '#85C1E2', // Sky Blue
        '#F8B88B', // Peach
        '#AED6F1', // Light Blue
        '#A9DFBF', // Light Green
        '#FAD7A0', // Light Orange
    ];

    // Get consistent color for a UUID
    const getUuidColor = (uuid) => {
        if (uuidColorMap[uuid]) {
            return uuidColorMap[uuid];
        }
        // Assign next available color
        const existingColors = Object.values(uuidColorMap);
        const availableColors = colorPalette.filter(color => !existingColors.includes(color));
        const color = availableColors.length > 0
            ? availableColors[0]
            : colorPalette[Object.keys(uuidColorMap).length % colorPalette.length];

        setUuidColorMap(prev => ({ ...prev, [uuid]: color }));
        return color;
    };

    const loadActivities = () => {
        setLoading(true);
        const query = `
            {
                gmactionlog(
                    where: [
                        {connective: BLANK, column: gmactionlog_aldepartmentid, operator: EQUAL, value: "${departmentId}"}
                    ],

                ) {
                    alid
                    aluserid
                    alusername
                    alactiontype
                    altargetid
                    aldepartmentid
                    altimestamp
                }
            }
        `;

        axiosGraphQLQuery(query, null, false)
            .then(response => {
                if (response.data.data.gmactionlog) {
                    const sortedActivities = response.data.data.gmactionlog.sort((a, b) => {
                        const dateA = new Date(a.altimestamp);
                        const dateB = new Date(b.altimestamp);
                        return dateB - dateA; // DESC order (newest first)
                    });

                    // Pre-assign colors to all unique UUIDs
                    const uniqueUuids = [...new Set(sortedActivities.map(a => a.altargetid))];
                    const newColorMap = {};
                    uniqueUuids.forEach((uuid, index) => {
                        newColorMap[uuid] = colorPalette[index % colorPalette.length];
                    });
                    setUuidColorMap(newColorMap);

                    setActivities(sortedActivities);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Error loading activities:', err);
                setLoading(false);
            });
    };

    const handleToggleExpand = (activityId, reportId) => {
        const isExpanded = expandedItems[activityId];

        setExpandedItems(prev => ({
            ...prev,
            [activityId]: !isExpanded
        }));

        // Load report details if expanding and not already loaded
        if (!isExpanded && !reportDetails[reportId]) {
            loadReportDetails(reportId);
        }
    };

    const loadReportDetails = (reportId) => {
        setLoadingDetails(prev => ({ ...prev, [reportId]: true }));

        const query = `
            {
                gmeventlog(
                    where: [
                        {connective: BLANK, column: gmeventlog_gmeid, operator: EQUAL, value: "${reportId}"}
                    ]
                ) {
                    gmeid
                    gmeusername
                    gmeuserlevel
                    gmestatus
                    gmecreated
                    gmesubselection
                    gmeanswers
                }
            }
        `;

        axiosGraphQLQuery(query, null, false)
            .then(response => {
                if (response.data.data.gmeventlog && response.data.data.gmeventlog.length > 0) {
                    setReportDetails(prev => ({
                        ...prev,
                        [reportId]: response.data.data.gmeventlog[0]
                    }));
                }
                setLoadingDetails(prev => ({ ...prev, [reportId]: false }));
            })
            .catch(err => {
                console.error('Error loading report details:', err);
                setLoadingDetails(prev => ({ ...prev, [reportId]: false }));
            });
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp + "Z");
        return date.toLocaleString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getActionText = (actionType) => {
        switch (actionType) {
            case 'CREATE':
                return 'Erstellt';
            case 'DELETE':
                return 'Gelöscht';
            default:
                return actionType;
        }
    };

    const renderReportDetails = (report) => {
        if (!report) return null;

        return (
            <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Vollständige ID:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                            {report.gmeid}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Status:</strong>
                        </Typography>
                        <Typography variant="body2">
                            <Chip
                                label={report.gmestatus === 'wip' ? 'Offen' : report.gmestatus === 'deleted' ? 'Gelöscht' : 'Abgeschlossen'}
                                size="small"
                                color={report.gmestatus === 'wip' ? 'warning' : report.gmestatus === 'deleted' ? 'error' : 'success'}
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Fallart:</strong>
                        </Typography>
                        <Typography variant="body2">
                            {report.gmesubselection && report.gmesubselection !== "undefined"
                                ? report.gmesubselection
                                : "Nicht vergeben"}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Erstellt am:</strong>
                        </Typography>
                        <Typography variant="body2">
                            {formatTimestamp(report.gmecreated)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Rolle:</strong>
                        </Typography>
                        <Typography variant="body2">
                            {report.gmeuserlevel === 'member' ? 'Mitglied' : 'Leiter'}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const open = Boolean(anchorEl);
    const id = open ? 'activity-log-popover' : undefined;

    return (
        <>
            <IconButton
                aria-describedby={id}
                onClick={handleClick}
                color="inherit"
                size="large"
                style={{ marginRight: 10 }}
            >
                <History />
            </IconButton>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ width: 400, maxHeight: 500, overflow: 'auto' }}>
                    <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
                        <Typography variant="h6">Aktivitätsverlauf</Typography>
                    </Box>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {activities.length === 0 ? (
                                <ListItem>
                                    <ListItemText primary="Keine Aktivitäten vorhanden" />
                                </ListItem>
                            ) : (
                                activities.map((activity, index) => (
                                    <React.Fragment key={activity.alid}>
                                        <ListItem
                                            alignItems="flex-start"
                                            sx={{
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {activity.alusername} - {getActionText(activity.alactiontype)}
                                                            </Typography>
                                                            <Chip
                                                                label={activity.altargetid.substring(0, 8)}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getUuidColor(activity.altargetid),
                                                                    color: '#000',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.7rem',
                                                                    height: '20px'
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" component="span" color="text.secondary">
                                                            {formatTimestamp(activity.altimestamp)}
                                                        </Typography>
                                                    }
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleExpand(activity.alid, activity.altargetid)}
                                                    sx={{ ml: 1 }}
                                                >
                                                    {expandedItems[activity.alid] ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                            </Box>
                                            <Collapse in={expandedItems[activity.alid]} timeout="auto" unmountOnExit>
                                                <Box sx={{
                                                    backgroundColor: 'grey.200',
                                                    mt: 1.5,
                                                    mb: 1,
                                                    mx: 1,
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.400',
                                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                                                }}>
                                                    {loadingDetails[activity.altargetid] ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                            <CircularProgress size={20} />
                                                        </Box>
                                                    ) : (
                                                        renderReportDetails(reportDetails[activity.altargetid])
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </ListItem>
                                        {index < activities.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default ActivityLogPopover;
